"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import {
  initializePaddle,
  type InitializePaddleOptions,
  type Paddle,
} from "@paddle/paddle-js";
import { toast } from "sonner";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { useRevalidateTables } from "@supabase-cache-helpers/postgrest-react-query";
import { pushGtmEvent } from "@/lib/utils/gtm";
import { resolvePurchaseParams } from "@/lib/utils/purchaseEvent";

export type CheckoutSyncState =
  | "idle"
  | "completed_waiting_close"
  | "refreshing";

type PaddleContextType = {
  paddle: Paddle | undefined;
  checkoutSyncState: CheckoutSyncState;
  clearCheckoutSyncState: () => void;
  openCheckout: (params: {
    priceId: string;
    organizationId: string;
    customerEmail?: string;
    customerId?: string;
    onDismissed?: () => void;
  }) => void;
  openPaymentMethodUpdate: (subscriptionId: string) => void;
};

const PaddleContext = createContext<PaddleContextType>({
  paddle: undefined,
  checkoutSyncState: "idle",
  clearCheckoutSyncState: () => {},
  openCheckout: () => {},
  openPaymentMethodUpdate: () => {},
});

export function PaddleProvider({ children }: PropsWithChildren) {
  const [paddle, setPaddle] = useState<Paddle>();
  const [checkoutSyncState, setCheckoutSyncState] =
    useState<CheckoutSyncState>("idle");
  const isInitialized = useRef(false);
  const shouldRefreshOnCheckoutClose = useRef(false);
  const onCheckoutDismissedRef = useRef<(() => void) | null>(null);
  // priceId of the open checkout — resolves plan/interval for the `purchase`
  // event, since checkout.completed doesn't carry them.
  const lastCheckoutPriceIdRef = useRef<string | null>(null);
  const router = useRouter();
  const queryClient = useQueryClient();
  const revalidateSubscriptionQuery = useRevalidateTables([
    { table: "organization_subscriptions", schema: "public" },
    { table: "apps", schema: "public" },
  ]);

  const revalidateCache = useCallback(async () => {
    await Promise.allSettled([
      queryClient.invalidateQueries({
        queryKey: ["transactionHistory"],
      }),
      revalidateSubscriptionQuery(),
    ]);
  }, [queryClient, revalidateSubscriptionQuery]);

  const clearCheckoutSyncState = useCallback(() => {
    shouldRefreshOnCheckoutClose.current = false;
    setCheckoutSyncState("idle");
  }, []);

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const token = process.env.NEXT_PUBLIC_PADDLE_CLIENT_TOKEN;
    if (!token) return;

    const environment =
      process.env.NEXT_PUBLIC_PADDLE_ENVIRONMENT === "production"
        ? "production"
        : "sandbox";

    const paddleOptions: InitializePaddleOptions = {
      environment,
      token,
      eventCallback: (event) => {
        if (event.name === "checkout.completed") {
          shouldRefreshOnCheckoutClose.current = true;
          setCheckoutSyncState("completed_waiting_close");
          toast.success("Subscription activated successfully!");

          // `purchase` event (starter/scale). Enterprise is tracked server-side.
          const purchaseParams = resolvePurchaseParams(
            lastCheckoutPriceIdRef.current
          );
          if (purchaseParams) {
            pushGtmEvent("purchase", purchaseParams);
          }
          lastCheckoutPriceIdRef.current = null;

          // Immediate refresh — catches case where Paddle's backend already
          // processed the transaction by the time this event fires.
          revalidateCache();

          // Fallback refetch at 3s to cover webhook/Paddle eventual consistency.
          // React Query's structural sharing means the cache only updates
          // (and subscribers only re-render) if the returned data actually
          // changed, so this is a no-op when the first refresh already won.
          setTimeout(() => {
            revalidateCache();
          }, 3000);
        }

        if (event.name === "checkout.closed") {
          if (shouldRefreshOnCheckoutClose.current) {
            shouldRefreshOnCheckoutClose.current = false;
            setCheckoutSyncState("refreshing");
            router.refresh();
          } else {
            // Closed without completing — call the dismiss callback if set.
            onCheckoutDismissedRef.current?.();
          }
          onCheckoutDismissedRef.current = null;
        }
      },
    };

    initializePaddle(paddleOptions).then(
      (paddleInstance: Paddle | undefined) => {
        if (paddleInstance) {
          setPaddle(paddleInstance);
        }
      }
    );
  }, [queryClient, revalidateCache, router]);

  const openCheckout = ({
    priceId,
    organizationId,
    customerEmail,
    customerId,
    onDismissed,
  }: {
    priceId: string;
    organizationId: string;
    customerEmail?: string;
    customerId?: string;
    onDismissed?: () => void;
  }) => {
    if (!paddle) {
      toast.error("Payment system is not available. Please try again.");
      return;
    }

    onCheckoutDismissedRef.current = onDismissed ?? null;
    lastCheckoutPriceIdRef.current = priceId;

    paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customData: { organization_id: organizationId },
      customer: customerId
        ? { id: customerId }
        : customerEmail
          ? { email: customerEmail }
          : undefined,
    });
  };

  const openPaymentMethodUpdate = (transactionId: string) => {
    if (!paddle) {
      toast.error("Payment system is not available. Please try again.");
      return;
    }

    paddle.Checkout.open({
      transactionId,
    });
  };

  return (
    <PaddleContext.Provider
      value={{
        paddle,
        checkoutSyncState,
        clearCheckoutSyncState,
        openCheckout,
        openPaymentMethodUpdate,
      }}
    >
      {children}
    </PaddleContext.Provider>
  );
}

export const usePaddle = () => useContext(PaddleContext);
